import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import authenticationSlice from './authenticationSlice';
import navigationSlice from './navigationSlice';
import managingCompanySlice from './managingCompanySlice';
import createCompanySlice from './createCompanySlice';
import createEmployeeSlice from './createEmployeeSlice';
import createPayCalendarSlice from './createPayCalendarSlice';
import importEmployeesSlice from './importEmployeesSlice';
import createTopUpWalletSlice from './giftingSlice';
import rewardFlowSlice from './rewardFlowSlice';

import { AuthenticationSlice } from './authenticationSlice/types';
import { NavigationSlice } from './navigationSlice/types';
import { ManagingCompanySlice } from './managingCompanySlice/types';
import { CreateCompanySlice } from './createCompanySlice/types';
import { CreateEmployeeSlice } from './createEmployeeSlice/types';
import { CreatePayCalendarSlice } from './createPayCalendarSlice/types';
import { ImportEmployeesSlice } from './importEmployeesSlice/types';
import { TopUpWalletSlice } from './giftingSlice/types';
import { RewardFlowSlice } from './rewardFlowSlice/types';

type PersistedNavigationSlice = Pick<NavigationSlice, 'view'>;

export const useNavigationStore = create<
  NavigationSlice,
  [['zustand/devtools', never], ['zustand/persist', PersistedNavigationSlice]]
>(
  devtools(
    persist(
      (set, get, api) => ({
        ...navigationSlice(set, get, api),
      }),
      {
        name: 'navigation',
        // The reconciliation type only lasts while the admin stays in the reconciliations pages.
        partialize: ({ view }) => ({ view }),
      }
    )
  )
);

export const useManagingCompanyStore = create<
  ManagingCompanySlice,
  [['zustand/devtools', never], ['zustand/persist', ManagingCompanySlice]]
>(
  devtools(
    persist(
      (set, get, api) => ({
        ...managingCompanySlice(set, get, api),
      }),
      { name: 'managing-company' }
    )
  )
);

export const useCreateCompanyStore = create<CreateCompanySlice, [['zustand/devtools', never]]>(
  devtools((set, get, api) => ({
    ...createCompanySlice(set, get, api),
  }))
);

export const useTopUpWalletStore = create<TopUpWalletSlice, [['zustand/devtools', never]]>(
  devtools((set, get, api) => ({
    ...createTopUpWalletSlice(set, get, api),
  }))
);

// Carries employees and gift settings into the gift flow, e.g. when sending a gift again to the people it didn't pay.
export const useRewardFlowStore = create<RewardFlowSlice, [['zustand/devtools', never]]>(
  devtools((set, get, api) => ({
    ...rewardFlowSlice(set, get, api),
  }))
);

export const useCreateEmployeeStore = create<CreateEmployeeSlice, [['zustand/devtools', never]]>(
  devtools((set, get, api) => ({
    ...createEmployeeSlice(set, get, api),
  }))
);

export const useCreatePayCalendarStore = create<
  CreatePayCalendarSlice,
  [['zustand/devtools', never]]
>(
  devtools((set, get, api) => ({
    ...createPayCalendarSlice(set, get, api),
  }))
);

export const useImportEmployeesStore = create<ImportEmployeesSlice, [['zustand/devtools', never]]>(
  devtools((set, get, api) => ({
    ...importEmployeesSlice(set, get, api),
  }))
);

export const useAuthenticationStore = create<AuthenticationSlice, [['zustand/devtools', never]]>(
  devtools((set, get, api) => ({
    ...authenticationSlice(set, get, api),
  }))
);
